import { useState, useRef, useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function ResizableRenderer({ element }: RendererProps) {
  const { dispatchEvent } = useRenderContext()
  const [ratio, setRatio] = useState(0.5)
  const containerRef = useRef<HTMLDivElement>(null)

  const children = element.children ?? []
  const panels = children.filter((c) => c.type !== "separator")
  const handle = children.find((c) => c.type === "separator")

  const isVertical =
    element.className?.includes("flex-col") ||
    (element as any).direction === "vertical"

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()

      const onMouseMove = (ev: MouseEvent) => {
        const newRatio = isVertical
          ? Math.max(0.1, Math.min(0.9, (ev.clientY - rect.top) / rect.height))
          : Math.max(0.1, Math.min(0.9, (ev.clientX - rect.left) / rect.width))

        setRatio(newRatio)
        if (handle) {
          dispatchEvent(handle.id, "drag", { ratio: newRatio })
        }
      }

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove)
        document.removeEventListener("mouseup", onMouseUp)
      }

      document.addEventListener("mousemove", onMouseMove)
      document.addEventListener("mouseup", onMouseUp)
    },
    [isVertical, handle, dispatchEvent]
  )

  if (panels.length < 2) {
    return (
      <div className={element.className} data-testid={`resizable-${element.id}`}>
        {children.map((c) => (
          <ElementRenderer key={c.id} element={c} />
        ))}
      </div>
    )
  }

  const firstPercent = `${ratio * 100}%`
  const secondPercent = `${(1 - ratio) * 100}%`

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex overflow-hidden",
        isVertical ? "flex-col" : "flex-row",
        element.className
      )}
      data-testid={`resizable-${element.id}`}
      style={{ minHeight: isVertical ? 200 : undefined, minWidth: isVertical ? undefined : 200 }}
      aria-label={element.ariaLabel ?? "Resizable panels"}
    >
      <div style={isVertical ? { height: firstPercent } : { width: firstPercent }} className="overflow-hidden">
        <ElementRenderer element={panels[0]} />
      </div>
      <div
        className={cn(
          "flex-shrink-0 bg-border hover:bg-primary/20 transition-colors",
          isVertical
            ? "h-2 cursor-row-resize flex items-center justify-center"
            : "w-2 cursor-col-resize flex items-center justify-center"
        )}
        onMouseDown={handleMouseDown}
        role="separator"
        aria-label={handle?.ariaLabel ?? "Resize handle"}
      >
        <div
          className={cn(
            "rounded-full bg-muted-foreground/50",
            isVertical ? "w-8 h-1" : "w-1 h-8"
          )}
        />
      </div>
      <div style={isVertical ? { height: secondPercent } : { width: secondPercent }} className="overflow-hidden">
        <ElementRenderer element={panels[1]} />
      </div>
    </div>
  )
}
